import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axios';
import { toastError, toastSuccess } from '../../../utils/toast';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import useConfirmModal from '../../../components/ConfirmModal/useConfirmModal';
import { enrichListing } from '../groupings';
import DrawerHeader from './DrawerHeader';
import DrawerFooter from './DrawerFooter';
import ReasonBanner from './DrawerSections/ReasonBanner';
import LiveEditPolicyCard from './DrawerSections/LiveEditPolicyCard';
import BulkChecklist from './DrawerSections/BulkChecklist';
import SummaryGrid from './DrawerSections/SummaryGrid';
import AiFieldsSection from './DrawerSections/AiFieldsSection';
import AmenitiesSection from './DrawerSections/AmenitiesSection';
import AvailabilitySection from './DrawerSections/AvailabilitySection';
import PropertyDetailsPreview from './DrawerSections/PropertyDetailsPreview';
import GeofencePreview from './DrawerSections/GeofencePreview';
import MaxSpotsSection from './DrawerSections/MaxSpotsSection';
import RevenueEstimateSection from './DrawerSections/RevenueEstimateSection';
import PhotosPreview from './DrawerSections/PhotosPreview';
import BlackoutPeriodEditor from './DrawerSections/BlackoutPeriodEditor';
import InsuranceSection from './DrawerSections/InsuranceSection';
import Timeline from './DrawerSections/Timeline';
import { routeForEditFromDrawer } from '../routing';
import {
    buildInitialDirtyFields,
    buildSavePayload,
    getLivePhotoBlockingError,
    hasDirtyChanges,
    isBulkDraftListing,
    isLiveEditableListing,
    mergeDirtyFieldsIntoListing,
    validateDirtyFieldsClient,
} from './drawerEditHelpers';
import { insuranceToPayload } from '../../../utils/insuranceHelpers';
import './Drawer.css';

export default function PropertyDrawer({ listing, bulkDrafts = [], onSubmitted, onListingUpdated }) {
    const navigate = useNavigate();
    const discardModal = useConfirmModal();

    const [localListing, setLocalListing] = useState(listing);
    const [editMode, setEditMode] = useState(false);
    const [baselineFields, setBaselineFields] = useState(null);
    const [dirtyFields, setDirtyFields] = useState(null);
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        setLocalListing(listing);
        setEditMode(false);
        setBaselineFields(null);
        setDirtyFields(null);
        setFieldErrors({});
    }, [listing?._id]);

    useEffect(() => {
        if (listing) {
            setLocalListing(listing);
        }
    }, [listing]);

    // InsuranceSection autosaves per Yes/No click and field blur rather than
    // through one big explicit Save, so several PUTs can be in flight at
    // once. A ref (not the `localListing` closure a handler captured at call
    // time) is what lets a late response tell whether the drawer has since
    // moved on to a different listing.
    const currentListingIdRef = useRef(localListing?._id);
    useEffect(() => {
        currentListingIdRef.current = localListing?._id;
    }, [localListing?._id]);

    const isBulkDraft = isBulkDraftListing(localListing);
    const isLiveEditable = isLiveEditableListing(localListing);
    // editMode is shared with bulk drafts but governs different sections —
    // see the body render below. `editMode && isLiveEditable` is the
    // live-edit path; `editMode && isBulkDraft` is the bulk-draft path.
    const liveEditMode = editMode && isLiveEditable;

    const hasDirty = useMemo(
        () => editMode && hasDirtyChanges(baselineFields, dirtyFields),
        [editMode, baselineFields, dirtyFields],
    );

    const displayListing = useMemo(
        () => mergeDirtyFieldsIntoListing(localListing, dirtyFields),
        [localListing, dirtyFields],
    );

    const exitEditMode = useCallback(() => {
        setEditMode(false);
        setBaselineFields(null);
        setDirtyFields(null);
        setFieldErrors({});
    }, []);

    const enterEditMode = useCallback(() => {
        if (!localListing) return;
        // Phase 1 — both bulk drafts and live-editable listings use the
        // inline drawer edit mode. Other statuses (pending, approved,
        // attention) still route to the wizard via requestEdit.
        if (!isBulkDraftListing(localListing) && !isLiveEditableListing(localListing)) return;
        const initial = buildInitialDirtyFields(localListing);
        setBaselineFields(initial);
        setDirtyFields(initial);
        setFieldErrors({});
        setEditMode(true);
    }, [localListing]);

    const requestEdit = useCallback(() => {
        if (!localListing) return;
        if (isBulkDraftListing(localListing) || isLiveEditableListing(localListing)) {
            enterEditMode();
            return;
        }
        navigate(routeForEditFromDrawer(localListing, {
            edit: localListing._v5Status === 'attention',
        }));
    }, [enterEditMode, localListing, navigate]);

    const requestEditWithSeed = useCallback(() => {
        if (!localListing) return null;
        if (isBulkDraftListing(localListing) || isLiveEditableListing(localListing)) {
            const initial = buildInitialDirtyFields(localListing);
            setBaselineFields(initial);
            setDirtyFields(initial);
            setFieldErrors({});
            setEditMode(true);
            return initial;
        }
        requestEdit();
        return null;
    }, [localListing, requestEdit]);

    const confirmDiscard = useCallback((onConfirm) => {
        if (editMode && hasDirty) {
            discardModal.confirm({
                title: 'Discard unsaved changes?',
                message: 'Your edits will be lost.',
                confirmLabel: 'Discard',
                cancelLabel: 'Keep editing',
                variant: 'danger',
                onConfirm,
            });
            return;
        }
        onConfirm();
    }, [discardModal, editMode, hasDirty]);

    const navigateAway = useCallback(() => {
        navigate('/dashboard/my-properties');
    }, [navigate]);

    const requestClose = useCallback(() => {
        confirmDiscard(() => {
            exitEditMode();
            navigateAway();
        });
    }, [confirmDiscard, exitEditMode, navigateAway]);

    const handleCancelEdits = useCallback(() => {
        confirmDiscard(exitEditMode);
    }, [confirmDiscard, exitEditMode]);

    const applyListingUpdate = useCallback((updated) => {
        const enriched = enrichListing(updated) || updated;
        setLocalListing(enriched);
        onListingUpdated?.(updated);
    }, [onListingUpdated]);

    // PP-419 — the photos modal calls this instead of PUTting on its own.
    // Photos are staged in dirtyFields.photos and travel with the drawer's
    // Save Changes PUT, so pricing / spots / instructions / photos commit
    // atomically. Requires the drawer to already be in edit mode (the
    // modal's Edit button only surfaces in editMode).
    const stagePhotos = useCallback((merged) => {
        if (!Array.isArray(merged)) return;
        setDirtyFields((prev) => ({
            ...(prev || {}),
            photos: merged,
        }));
    }, []);

    const handleSaveEdits = useCallback(async () => {
        if (!localListing || !dirtyFields || saving) return;

        const clientErrors = validateDirtyFieldsClient(dirtyFields, localListing);
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            return;
        }

        // PP-419 — evaluate against the merged view so newly-staged photos
        // (from the modal's Save) count against the gate, not just the
        // persisted listing.
        const mergedForGate = mergeDirtyFieldsIntoListing(localListing, dirtyFields);
        const photoBlockingError = getLivePhotoBlockingError(mergedForGate, isLiveEditable);
        if (photoBlockingError) {
            toastError(photoBlockingError);
            return;
        }

        // PP-342 Phase 1 — live-edit saves go through the same PUT
        // /listings/:id endpoint but with a filtered payload (insurance,
        // enrichmentFields, and confirmedFields-outside-allowlist stripped).
        const mode = isLiveEditable ? 'live' : 'bulk-draft';
        const payload = buildSavePayload(localListing, dirtyFields, { mode });
        if (Object.keys(payload).length === 0) {
            exitEditMode();
            return;
        }

        setSaving(true);
        setFieldErrors({});
        try {
            const res = await axios.put(`/listings/${localListing._id}`, payload);
            applyListingUpdate(res.data.listing);
            // Surface the live-sync soft-failure flag — see PP-342 sync
            // helper. The host's edit is saved on the Listing; the public
            // Property record didn't update. Phase 1 has no reconciliation
            // job, so don't promise an auto-retry the host won't get —
            // direct them to support if the change doesn't appear.
            if (res.data.propertySyncFailed) {
                toastError("Your edit was saved, but the public listing could not be updated. Contact support if your changes don't appear.");
            } else if (isLiveEditable) {
                toastSuccess('Listing updated.');
            }
            exitEditMode();
        } catch (err) {
            if (err?.response?.status === 422 && err.response.data?.errors) {
                setFieldErrors(err.response.data.errors);
            } else if (err?.response?.status === 422 && err.response.data?.disallowedField) {
                // Server rejected a field that should never reach here if the
                // client-side filter is correct. Surface generically.
                toastError(`Cannot edit ${err.response.data.disallowedField} on a live listing.`);
            } else {
                toastError('Could not save changes');
            }
        } finally {
            setSaving(false);
        }
    }, [applyListingUpdate, dirtyFields, exitEditMode, isLiveEditable, localListing, saving]);

    const handleGeofenceSaved = useCallback(() => {
        setLocalListing((prev) => ({
            ...prev,
            enrichmentFields: {
                ...(prev.enrichmentFields || {}),
                ingressEgressMethod: 'host_set',
                ingressEgressConfidence: 'high',
            },
        }));
    }, []);

    const handleGeofenceModalClose = useCallback(() => {
        const id = localListing?._id;
        if (!id) return;
        axios.get(`/listings/${id}`)
            .then((res) => {
                if (res?.data?.listing) {
                    applyListingUpdate(res.data.listing);
                }
            })
            .catch(() => {});
    }, [localListing?._id, applyListingUpdate]);

    // Returns whether the save actually succeeded so callers (InsuranceSection's
    // accept flow) can avoid recording an acceptance against entity fields that
    // silently failed to persist.
    const handleInsurancePersist = useCallback(async (insurance) => {
        const id = localListing?._id;
        if (!id) return false;
        try {
            const res = await axios.put(`/listings/${id}`, {
                insurance: insuranceToPayload(insurance),
            });
            if (currentListingIdRef.current === id) {
                applyListingUpdate(res.data.listing);
            }
            return true;
        } catch {
            toastError('Could not save insurance details.');
            return false;
        }
    }, [applyListingUpdate, localListing?._id]);

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (e.target.closest('input, textarea, select')) return;
            if (document.querySelector('.geofence-draw-modal-root')) return;
            if (document.querySelector('.photos-editor-modal-root')) return;
            requestClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [requestClose]);

    if (!listing) {
        return (
            <div
                className="property-drawer-root host-panel-overlay open"
                onClick={(e) => {
                    if (e.target === e.currentTarget) navigateAway();
                }}
            >
                <aside className="host-panel" role="dialog" aria-labelledby="property-drawer-title">
                    <header className="host-panel-head">
                        <div className="host-panel-head-row">
                            <div className="host-panel-head-meta">
                                <h3 id="property-drawer-title">Listing not found</h3>
                                <div className="host-panel-head-sub">
                                    This listing could not be loaded. Close to return to My Properties.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="host-panel-close"
                                onClick={navigateAway}
                                aria-label="Close drawer"
                            >
                                ×
                            </button>
                        </div>
                    </header>
                    <footer className="host-panel-footer">
                        <button type="button" className="btn btn-secondary" onClick={navigateAway}>
                            Close
                        </button>
                    </footer>
                </aside>
            </div>
        );
    }

    const showReason = localListing._v5Status === 'attention';
    const showBulkChecklist = isBulkDraft;

    const sectionProps = {
        listing: localListing,
        editMode: editMode && (isBulkDraft || isLiveEditable),
        dirtyFields,
        baselineFields,
        setDirtyFields,
        fieldErrors,
        onRequestEdit: requestEdit,
        onRequestEditWithSeed: requestEditWithSeed,
    };

    return (
        <div
            className="property-drawer-root host-panel-overlay open"
            onClick={(e) => {
                if (e.target === e.currentTarget) requestClose();
            }}
        >
            <aside className="host-panel" role="dialog" aria-labelledby="property-drawer-title">
                <DrawerHeader
                    listing={localListing}
                    editMode={editMode}
                    onClose={requestClose}
                    onRequestEdit={requestEdit}
                    onCancelEdits={handleCancelEdits}
                />
                <div className="host-panel-body">
                    {showReason && <ReasonBanner listing={localListing} />}
                    {isBulkDraft ? (
                        <>
                            {showBulkChecklist && <BulkChecklist listing={displayListing} />}
                            <SummaryGrid {...sectionProps} />
                            <AiFieldsSection {...sectionProps} />
                            <AmenitiesSection {...sectionProps} />
                            <AvailabilitySection {...sectionProps} />
                            <GeofencePreview
                                {...sectionProps}
                                onGeofenceSaved={handleGeofenceSaved}
                                onGeofenceModalClose={handleGeofenceModalClose}
                            />
                            <MaxSpotsSection {...sectionProps} />
                            <RevenueEstimateSection {...sectionProps} />
                            <PhotosPreview
                                listing={displayListing}
                                editMode={editMode && isBulkDraft}
                                onStagePhotos={stagePhotos}
                            />
                            <InsuranceSection
                                listing={localListing}
                                editMode={editMode}
                                dirtyFields={dirtyFields}
                                setDirtyFields={setDirtyFields}
                                forceEditable={isBulkDraft}
                                onPersist={isBulkDraft ? handleInsurancePersist : undefined}
                                bulkDraftCount={bulkDrafts.length}
                                bulkDraftIds={bulkDrafts.map((d) => d._id)}
                                onBulkListingsUpdated={() => {
                                    axios.get('/listings')
                                        .then((res) => {
                                            const updated = (res.data.listings || []).find(
                                                (l) => l._id === localListing._id,
                                            );
                                            if (updated) applyListingUpdate(updated);
                                        })
                                        .catch(() => {});
                                }}
                            />
                            <Timeline listing={localListing} />
                        </>
                    ) : liveEditMode ? (
                        // PP-342 Phase 1 — live-edit subset. SummaryGrid
                        // carries the inline editors for hourly rate and
                        // parking instructions; geofence / insurance /
                        // AI-fields (name, type, entrance, surface, fuel)
                        // and full Availability are intentionally NOT editable
                        // here. PP-441/442 — blackout dates (Phase 2) are now
                        // editable via BlackoutPeriodEditor without pausing
                        // the listing or triggering admin review.
                        <>
                            <SummaryGrid {...sectionProps} />
                            <MaxSpotsSection {...sectionProps} />
                            <AmenitiesSection {...sectionProps} />
                            <PhotosPreview
                                listing={displayListing}
                                editMode
                                onStagePhotos={stagePhotos}
                            />
                            <BlackoutPeriodEditor listing={localListing} />
                            <RevenueEstimateSection {...sectionProps} />
                            <Timeline listing={localListing} />
                        </>
                    ) : (
                        <>
                            {/* PP-403 — explain to the host which fields are
                                editable inline vs. which require pausing the
                                listing + admin review. Only on live drawers;
                                hidden on paused / paused_review where the
                                footer already drives the next step. */}
                            {localListing._v5Status === 'live' && <LiveEditPolicyCard />}
                            {/* PP-342: surface Type / Spots / Property name / Parking
                                instructions inside SummaryGrid even in the non-edit
                                live view (drop the previewMode gate that previously
                                deferred them to PropertyDetailsPreview). The
                                amenities + availability summary chips PropertyDetailsPreview
                                still adds aren't duplicated by SummaryGrid, so it stays. */}
                            <SummaryGrid {...sectionProps} />
                            <PropertyDetailsPreview listing={displayListing} />
                            <GeofencePreview
                                listing={localListing}
                                editMode={false}
                                onGeofenceSaved={handleGeofenceSaved}
                                onGeofenceModalClose={handleGeofenceModalClose}
                            />
                            <RevenueEstimateSection {...sectionProps} />
                            <PhotosPreview
                                listing={localListing}
                                editMode={false}
                            />
                            <BlackoutPeriodEditor listing={localListing} readOnly />
                            <Timeline listing={localListing} />
                        </>
                    )}
                </div>
                <DrawerFooter
                    listing={localListing}
                    onClose={requestClose}
                    onSubmitted={onSubmitted}
                    editMode={editMode && (isBulkDraft || isLiveEditable)}
                    isLiveEdit={liveEditMode}
                    canLiveEdit={isLiveEditable}
                    onEnterEditMode={enterEditMode}
                    onSaveEdits={handleSaveEdits}
                    onCancelEdits={handleCancelEdits}
                    saving={saving}
                    hasDirty={hasDirty}
                />
            </aside>
            <ConfirmModal {...discardModal.props} />
        </div>
    );
}
